import React, { useEffect, useMemo, useState } from 'react';
import { Select, message } from 'antd';
import _ from 'lodash';
import { AntButton } from '@/components/antd';
import { defaultAxios } from '@/utils';
import { TableLv2 } from '../../table/level2';

interface ITeamAdminData {
    name: string;
    phone: number | string;
    id: string | number;
    admin_uid: number;
}

interface IRightsReportTitle {
    name: string;
    key: string;
}

interface IRightsReportData {
    total: number;
    titles: IRightsReportTitle[];
    data: any[];
}

interface IRightsReportDetailProps {
    teamAdminData: ITeamAdminData[];
    teamAdminLoading: boolean;
}

const DEFAULT_TITLES: IRightsReportTitle[] = [
    { name: '团队ID', key: 'co_id' },
    { name: '团队名称', key: 'team_name' },
    { name: '权益类型', key: 'equity_type' },
    { name: '管理员', key: 'admin' },
    { name: '权益量', key: 'equity_num' },
    { name: '开通时间', key: 'open_time' },
    { name: '到期时间', key: 'expire_time' },
];

const COLUMN_WIDTH_MAP: { [key: string]: number } = {
    co_id: 100,
    team_name: 180,
    equity_type: 120,
    admin: 160,
    equity_num: 120,
    open_time: 180,
    expire_time: 180,
};

const DEFAULT_PAGINATION = {
    current: 1,
    pageSize: 20,
};

const RightsReportDetail: React.FC<IRightsReportDetailProps> = ({
    teamAdminData,
    teamAdminLoading,
}) => {
    const [selectedTeamId, setSelectedTeamId] = useState<string | number>();
    const [rightsReportLoading, setRightsReportLoading] = useState(false);
    const [rightsReportData, setRightsReportData] = useState<
        IRightsReportData
    >();
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

    const selectedAdmin = useMemo(
        () => teamAdminData.find(item => item.id === selectedTeamId),
        [teamAdminData, selectedTeamId],
    );

    const fetchRightsReportData = async (
        teamId: string | number | undefined = selectedTeamId,
        current: number = pagination.current,
        pageSize: number = pagination.pageSize,
    ) => {
        const admin = teamAdminData.find(item => item.id === teamId);
        if (!admin) {
            setRightsReportData(undefined);
            return;
        }

        setRightsReportLoading(true);
        const [d] = await defaultAxios.get(
            '/crm/equity/getRightsReportLicenseDetail',
            {
                co_id: admin.id,
                admin_uid: admin.admin_uid,
                page: current - 1,
                size: pageSize,
            },
            {
                timeout: 400000,
            },
        );
        setRightsReportLoading(false);

        if (d === null || d.data === null) {
            message.error('查询失败～');
            return;
        }

        const data = d.data;
        if (data && data.code === 0 && Array.isArray(data.data)) {
            setRightsReportData(data);
            return;
        }

        setRightsReportData({
            total: 0,
            titles: DEFAULT_TITLES,
            data: [],
        });
        message.error(data?.msg || '查询失败～');
    };

    useEffect(() => {
        if (!teamAdminData.length) {
            setSelectedTeamId(undefined);
            setRightsReportData(undefined);
            return;
        }

        const selectedAdminExists = teamAdminData.some(
            item => item.id === selectedTeamId,
        );
        if (!selectedAdminExists) {
            setSelectedTeamId(teamAdminData[0].id);
            setPagination(DEFAULT_PAGINATION);
        }
    }, [teamAdminData, selectedTeamId]);

    useEffect(() => {
        if (selectedTeamId !== undefined && selectedTeamId !== null) {
            fetchRightsReportData(
                selectedTeamId,
                pagination.current,
                pagination.pageSize,
            );
        }
    }, [selectedTeamId, pagination.current, pagination.pageSize]);

    const columns = (rightsReportData?.titles || DEFAULT_TITLES).map(title => ({
        title: title.name,
        dataIndex: title.key,
        key: title.key,
        width: COLUMN_WIDTH_MAP[title.key] || 120,
    }));

    const dataSource = rightsReportData?.data?.map((item, index) => ({
        id: `${item.co_id || selectedTeamId || ''}-${item.admin_uid ||
            selectedAdmin?.admin_uid ||
            ''}-${item.open_time || ''}-${item.expire_time || ''}-${index}`,
        ...item,
    }));

    return (
        <div className="mai-rights-item">
            <p className="mai-rights-title">权益数据</p>
            <div className="mai-rights-filter">
                <div className="mai-rights-filter-item">
                    <span className="mai-rights-filter-label">团队管理员</span>
                    <Select
                        loading={teamAdminLoading}
                        value={selectedTeamId}
                        onChange={(value: any) => {
                            setSelectedTeamId(value);
                            setPagination(DEFAULT_PAGINATION);
                        }}
                        style={{
                            width: '150px',
                        }}
                        dropdownMatchSelectWidth={false}
                    >
                        {_.map(teamAdminData, item => (
                            <Select.Option
                                key={item.id}
                                value={item.id}
                            >{`${item.name}-${item.phone}`}</Select.Option>
                        ))}
                    </Select>
                </div>
                <AntButton
                    disabled={!selectedAdmin}
                    type="primary"
                    onClick={() => {
                        if (pagination.current === 1) {
                            fetchRightsReportData();
                            return;
                        }
                        setPagination({
                            ...pagination,
                            current: 1,
                        });
                    }}
                >
                    查询
                </AntButton>
            </div>
            <div style={{ marginBottom: '15px' }}>
                <TableLv2
                    closeRowSelection={true}
                    className={'talent-member-data-table'}
                    loading={rightsReportLoading}
                    dataSource={dataSource}
                    scroll={{
                        x: 1100,
                    }}
                    pagination={{
                        total: rightsReportData?.total || 0,
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                    }}
                    onPaginationChange={({ current, pageSize }) => {
                        setPagination({
                            current,
                            pageSize: pageSize!,
                        });
                    }}
                    columns={columns}
                />
            </div>
        </div>
    );
};

export default RightsReportDetail;
